import Topbar from "../../layout/Topbar";
import SettingsSidebar from "../SettingsSidebar";
import React, {useEffect, useState} from "react";
import {CssBaseline, Box, Button, Chip, FormControlLabel, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, Typography} from "@mui/material";
import CustomSnackbar from "../../alert/CustomSnackbar";
import {connect} from "react-redux";
import {integrationActions} from "../../../store/actions/integrationAction";
import {DeleteOutlined, EditOutlined, RefreshOutlined} from "@mui/icons-material";
import Spinner from "../../spinner/Spinner";
import moment from "moment";
import AffiliateNetworkNoIntegrationsView from "./affiliate-network/AffiliateNetworkNoIntegrationsView";
import AffiliateNetworkIntegrations from "./affiliate-network/AffiliateNetworkIntegrations";
import {alertActions} from "../../../store/actions/alertActions";
import {SettingsContentBox, SettingsRoot} from "../General";
import RsocNoIntegrationsView from "./rsoc/RsocNoIntegrationsView";
import RsocIntegrations from "./rsoc/RsocIntegrations";
import ServerNoIntegrationsView from "./server/ServerNoIntegrationsView";
import ServerIntegrations from "./server/ServerIntegrations";
import {settingsPageStyles, SettingsPageHeader, getSettingsColors, settingsTabButtonStyles} from "../SettingsDesign";
import {IntegrationEmptyCard, IntegrationCard, IntegrationSectionHeader, IntegrationDeleteDialog} from "./IntegrationDesign";
import {alpha} from "@mui/material/styles";

export function Integrations(props) {
    const {
        alert,
        integration,
        getTrafficSourceIntegrations,
        getAffiliateNetworkIntegrations,
        getRsocIntegrations,
        deleteIntegration,
        enableDataTransfer,
        getRenewableFacebookLoginLink,
        getRenewableTikTokLoginLink,
        getRenewableGoogleLoginLink,
        getRenewableMicrosoftLoginLink,
        clear,
        getServerIntegrations
    } = props;

    /* Force to open modal with parent route on the background */
    useEffect(() => {
        if (props.location.pathname !== props.match.path) {
            props.history.push({
                pathname: props.location.pathname,
                state: {background: props.location}
            });
        }
    }, [props.location, props.history, props.match.path])

    const [category, setCategory] = useState('traffic');
    const categories = [
        {id: 'traffic', title: 'Traffic sources', items: integration?.integrations, loading: integration?.loading?.integrations, load: getTrafficSourceIntegrations},
        {id: 'affiliate', title: 'Affiliate networks', items: integration?.affiliateNetworkIntegrations, loading: integration?.loading?.affiliateNetworkIntegrations, load: getAffiliateNetworkIntegrations},
        {id: 'rsoc', title: 'RSOC', items: integration?.rsocIntegrations, loading: integration?.loading?.rsocIntegrations, load: getRsocIntegrations},
        {id: 'server', title: 'Servers & domains', items: integration?.serverIntegrations, loading: integration?.loading?.serverIntegrations, load: getServerIntegrations}
    ];
    const active = categories.find(item => item.id === category);
    const {items: activeItems, loading: activeLoading, load: loadActiveCategory} = active;
    useEffect(() => {
        if (activeItems === null && !activeLoading) loadActiveCategory();
    }, [activeItems, activeLoading, loadActiveCategory]);
    const handleCategoryKeyDown = event => {
        const index = categories.findIndex(item => item.id === category);
        const next = {ArrowRight: (index + 1) % categories.length, ArrowLeft: (index + categories.length - 1) % categories.length,
            Home: 0, End: categories.length - 1}[event.key];
        if (next === undefined) return;
        event.preventDefault();
        setCategory(categories[next].id);
        event.currentTarget.parentElement.querySelectorAll('[role="tab"]')[next].focus();
    };
    return <SettingsRoot>
        <CssBaseline/>
        <Topbar/>
        <SettingsSidebar {...props}/>
        <SettingsContentBox sx={[settingsPageStyles, {'& .MuiTableContainer-root': {border: 0}}]}>
            <Box sx={{maxWidth: 1280, minWidth: 0, mx: 'auto', px: {xs: 0, sm: 3.75}, pt: 3}}>
                <SettingsPageHeader title="Integrations"/>
                <Typography sx={{fontSize: 14, color: 'text.secondary', whiteSpace: 'normal', mt: -1.5, mb: 3}}>Connect your ad platforms, revenue partners, hosting, and domains.</Typography>
                <Box role="tablist" aria-label="Integration categories" sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 0.5, mb: 3,
                    bgcolor: theme => getSettingsColors(theme).card, borderRadius: '10px', width: 'fit-content', maxWidth: '100%'}}>
                    {categories.map(item => <Button key={item.id} role="tab" id={`integration-tab-${item.id}`} aria-selected={category === item.id}
                        aria-controls={`integration-panel-${item.id}`} tabIndex={category === item.id ? 0 : -1}
                        onKeyDown={handleCategoryKeyDown} onClick={() => setCategory(item.id)} color="inherit"
                        sx={{...settingsTabButtonStyles, gap: 1,
                            bgcolor: category === item.id ? theme => alpha(theme.palette.primary.main, 0.1) : 'transparent',
                            color: category === item.id ? 'primary.main' : 'text.secondary'}}>
                        {item.title}
                    </Button>)}
                </Box>
                <Box role="tabpanel" id={`integration-panel-${category}`} aria-labelledby={`integration-tab-${category}`}>
                    {(active.loading || active.items === null) && !active.items?.length ? <IntegrationCard><Box sx={{display: 'flex', justifyContent: 'center', py: 8}}><Spinner/></Box></IntegrationCard> : <>
                        {category === 'traffic' && (active.items?.length ? <IntegrationsView integration={integration} data={integration.integrations}
                            history={props.history} location={props.location} deleteIntegration={deleteIntegration} enableDataTransfer={enableDataTransfer}
                            getRenewableFacebookLoginLink={getRenewableFacebookLoginLink} getRenewableTikTokLoginLink={getRenewableTikTokLoginLink}
                            getRenewableGoogleLoginLink={getRenewableGoogleLoginLink} getRenewableMicrosoftLoginLink={getRenewableMicrosoftLoginLink} clear={clear}/> : <NoIntegrationsView props={props}/>)}
                        {category === 'affiliate' && (active.items?.length ? <AffiliateNetworkIntegrations {...props}/> : <AffiliateNetworkNoIntegrationsView {...props}/>)}
                        {category === 'rsoc' && (active.items?.length ? <RsocIntegrations {...props}/> : <RsocNoIntegrationsView {...props}/>)}
                        {category === 'server' && (active.items?.length ? <ServerIntegrations {...props}/> : <ServerNoIntegrationsView {...props}/>)}
                    </>}
                </Box>
            </Box>
            {alert.errorMsg && <CustomSnackbar message={alert.errorMsg} timeout={3000}/>}
            {alert.successMsg && <CustomSnackbar severity="success" message={alert.successMsg} timeout={3000}/>}
        </SettingsContentBox>
    </SettingsRoot>;
}

function IntegrationsView(
    {
        data,
        history,
        location,
        deleteIntegration,
        enableDataTransfer,
        integration,
        getRenewableFacebookLoginLink,
        getRenewableTikTokLoginLink,
        getRenewableGoogleLoginLink,
        getRenewableMicrosoftLoginLink,
        clear
    }) {

    const [prompt, setPrompt] = useState(false);
    const [deleteIntegrationId, setDeleteIntegrationId] = useState(0);

    const addIntegration = () => {
        history.push({
            pathname: "/settings/integrations/add",
            state: {background: location}
        });
    };

    const account = (networkType, credentials) => {
        if (networkType === "facebook") {
            return credentials?.map((i) => {
                if (i?.keyName === "fb_ad_account_id") {
                    return i?.keyValue;
                }
                return "";
            });
        } else if (networkType === "google") {
            return credentials?.map((i) => {
                if (i?.keyName === "google_ad_account_id") {
                    const v = i?.keyValue;
                    return v.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
                }
                return "";
            });
        } else if (networkType === "microsoft") {
            return credentials?.map((i) => {
                if (i?.keyName === "microsoft_ad_account_id") {
                    return i?.keyValue;
                }
                return "";
            });
        } else if (networkType === "taboola") {
            return credentials?.map((i) => {
                if (i?.keyName === "taboola_account_id") {
                    return i?.keyValue;
                }
                return "";
            });
        } else if (networkType === "mgid") {
            return credentials?.map((i) => {
                if (i?.keyName === "mgid_account_id") {
                    return i?.keyValue;
                }
                return "";
            });
        } else if (networkType === "outbrain") {
            return credentials?.map((i) => {
                if (i?.keyName === "outbrain_account_name") {
                    return i?.keyValue;
                }
                return "";
            });
        } else if (networkType === "tiktok") {
            return credentials?.map((i) => {
                if (i?.keyName === "tt_ad_account_id") {
                    return i?.keyValue;
                }
                return "";
            });
        } else if (networkType === "revcontent") {
            return credentials?.map((i) => {
                if (i?.keyName === "revcontent_client_id") {
                    return i?.keyValue;
                }
                return "";
            });
        } else if (networkType === "outbrain_dsp") {
            return credentials?.map((i) => {
                if (i?.keyName === "outbrain_dsp_account_id") {
                    return i?.keyValue;
                }
                return "";
            });
        } else if (networkType === "newsbreak") {
            return credentials?.map((i) => {
                if (i?.keyName === "nb_account_id") {
                    return i?.keyValue;
                }
                return "";
            });
        } else if (networkType === "smartnews") {
            return credentials?.map((i) => {
                if (i?.keyName === "smartnews_ad_account_id") {
                    return i?.keyValue;
                }
                return "";
            });
        } else if (networkType === "mediago") {
            return credentials?.map((i) => {
                if (i?.keyName === "mediago_ad_account_id") {
                    return i?.keyValue;
                }
                return "";
            });
        } else if (networkType === "popin") {
            return credentials?.map((i) => {
                if (i?.keyName === "popin_ad_account_id") {
                    return i?.keyValue;
                }
                return "";
            });
        }
    };

    const renewAccessToken = (selectedIntegration) => {
        if (selectedIntegration?.networkType === "facebook") {
            return (
                <Box ml={1}>
                    <Tooltip title="Renew access token">
                    <span>
                        <IconButton
                            size="small"
                            color="inherit"
                            onClick={() => getRenewableFacebookLoginLink(selectedIntegration?.id)}
                            aria-label="renew-access-token"
                        >
                            <RefreshOutlined fontSize="small" color="secondary"/>
                        </IconButton>
                    </span>
                    </Tooltip>
                </Box>
            );
        }
        if (selectedIntegration?.networkType === "google") {
            return (
                <Box ml={1}>
                    <Tooltip title="Renew access token">
                    <span>
                        <IconButton
                            size="small"
                            color="inherit"
                            onClick={() => getRenewableGoogleLoginLink(selectedIntegration?.id)}
                            aria-label="renew-access-token"
                        >
                            <RefreshOutlined fontSize="small" color="secondary"/>
                        </IconButton>
                    </span>
                    </Tooltip>
                </Box>
            );
        }
        if (selectedIntegration?.networkType === "microsoft") {
            return (
                <Box ml={1}>
                    <Tooltip title="Renew access token">
                    <span>
                        <IconButton
                            size="small"
                            color="inherit"
                            onClick={() => getRenewableMicrosoftLoginLink(selectedIntegration?.id)}
                            aria-label="renew-access-token"
                        >
                            <RefreshOutlined fontSize="small" color="secondary"/>
                        </IconButton>
                    </span>
                    </Tooltip>
                </Box>
            );
        }
        if (selectedIntegration?.networkType === "tiktok") {
            return (
                <Box ml={1}>
                    <Tooltip title="Renew access token">
                    <span>
                        <IconButton
                            size="small"
                            color="inherit"
                            onClick={() => getRenewableTikTokLoginLink(selectedIntegration?.id)}
                            aria-label="renew-access-token"
                        >
                            <RefreshOutlined fontSize="small" color="secondary"/>
                        </IconButton>
                    </span>
                    </Tooltip>
                </Box>
            );
        }
    };

    const edit = (rowData) => {

        // Clear error/success messages.
        clear();

        if (rowData?.networkType === "facebook") {
            history.push({
                pathname: "/settings/integrations/facebook/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
        if (rowData?.networkType === "tiktok") {
            history.push({
                pathname: "/settings/integrations/tiktok/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
        if (rowData?.networkType === "google") {
            history.push({
                pathname: "/settings/integrations/google/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
        if (rowData?.networkType === "microsoft") {
            history.push({
                pathname: "/settings/integrations/microsoft/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
        if (rowData?.networkType === "pushground") {
            history.push({
                pathname: "/settings/integrations/pushground/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
        if (rowData?.networkType === "propeller-ads") {
            history.push({
                pathname: "/settings/integrations/propeller-ads/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
        if (rowData?.networkType === "exoclick") {
            history.push({
                pathname: "/settings/integrations/exoclick/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
        if (rowData?.networkType === "mondiad") {
            history.push({
                pathname: "/settings/integrations/mondiad/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
        if (rowData?.networkType === "taboola") {
            history.push({
                pathname: "/settings/integrations/taboola/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
        if (rowData?.networkType === "mgid") {
            history.push({
                pathname: "/settings/integrations/mgid/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
        if (rowData?.networkType === "outbrain") {
            history.push({
                pathname: "/settings/integrations/outbrain/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
        if (rowData?.networkType === "revcontent") {
            history.push({
                pathname: "/settings/integrations/revcontent/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
        if (rowData?.networkType === "outbrain_dsp") {
            history.push({
                pathname: "/settings/integrations/outbrain-dsp/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
        if (rowData?.networkType === "newsbreak") {
            history.push({
                pathname: "/settings/integrations/newsbreak/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
        if (rowData?.networkType === "smartnews") {
            history.push({
                pathname: "/settings/integrations/smartnews/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
        if (rowData?.networkType === "mediago") {
            history.push({
                pathname: "/settings/integrations/mediago/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
        if (rowData?.networkType === "popin") {
            history.push({
                pathname: "/settings/integrations/popin" +
                    "/" + parseInt(rowData?.id),
                state: {background: location}
            });
        }
    };

    const handleDeletePrompt = (id) => {
        setDeleteIntegrationId(id);
        setPrompt(true);
    };

    const handleDeleteIntegration = (prompt) => {
        if (!prompt) {
            setPrompt(false);
            return false;
        } else {
            deleteIntegration(deleteIntegrationId);
            setPrompt(false);
        }
    };

    const handleDataTransferSwitch = (id, status) => {
        if (status) {
            enableDataTransfer(id, false);
        } else {
            enableDataTransfer(id, true);
        }
    };

    useEffect(() => {
        if (integration?.facebook?.renewableLoginLink !== null) {
            document.location.href = integration?.facebook?.renewableLoginLink;
        }
        if (integration?.google?.renewableLoginLink !== null) {
            document.location.href = integration?.google?.renewableLoginLink;
        }
        if (integration?.microsoft?.renewableLoginLink !== null) {
            document.location.href = integration?.microsoft?.renewableLoginLink;
        }
        if (integration?.tiktok?.renewableLoginLink !== null) {
            document.location.href = integration?.tiktok?.renewableLoginLink;
        }
    }, [integration?.facebook?.renewableLoginLink, integration?.google?.renewableLoginLink, integration?.microsoft?.renewableLoginLink, integration?.tiktok?.renewableLoginLink])

    return (
        <Box sx={{minWidth: 0}}>

            <IntegrationCard elevation={0}>
                {integration?.loading.integrationsView && <Spinner overComponentBox={true}/>}
                <IntegrationSectionHeader title="Cost sync · Traffic sources" description="Sync advertising costs and manage your campaigns." onAdd={addIntegration}
                    count={integration?.loading?.integrations ? undefined : data?.length}/>

                {prompt && <IntegrationDeleteDialog question="Delete integration?" text={`Remove "${data?.find(row => row.id === deleteIntegrationId)?.networkName || 'this integration'}" from Skro?`} confirm={handleDeleteIntegration}/>
                }

                <TableContainer sx={{maxHeight: "400px"}}>
                    <Table stickyHeader size="small" aria-label="traffic-sources-integrations">
                        <TableHead sx={{whiteSpace: "nowrap"}}>
                            <TableRow hover>
                                <TableCell>Network Name</TableCell>
                                <TableCell>Ad Account</TableCell>
                                <TableCell>Sync Cost</TableCell>
                                <TableCell>Last Synced Time</TableCell>
                                <TableCell>Error</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{
                            whiteSpace: "nowrap",
                            maxHeight: "300px",
                            overflow: "auto"
                        }}>
                            {data?.map((row) => (
                                <TableRow key={row?.id}>
                                    <TableCell>{row?.networkName}</TableCell>
                                    <TableCell sx={{
                                        maxWidth: "200px",
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        textOverflow: "ellipsis"
                                    }}>
                                        {account(row?.networkType, row?.integrationCredentials)}
                                    </TableCell>
                                    <TableCell>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={row?.enableDataTransfer}
                                                    onChange={() => handleDataTransferSwitch(row?.id, row?.enableDataTransfer)}
                                                    name="enableDataTransfer"
                                                    color="primary"
                                                    size="small"
                                                />
                                            }
                                            label={row?.enableDataTransfer ? "On" : "Off"}
                                        />
                                    </TableCell>
                                    <TableCell>{row?.lastDataTransfer === null ? "-" : moment.utc(row?.lastDataTransfer).format("YYYY-MM-DD HH:mm") + " (UTC)"}</TableCell>
                                    <TableCell>
                                        {row?.hasError ?
                                            <Chip size="small" color="error" variant="outlined"
                                                  label="Token expired"/> : "-"}
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" justifyContent="right">
                                            <Box mr={1}>
                                                <Tooltip title="Edit">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="inherit"
                                                        onClick={() => edit(row)}
                                                        aria-label="edit"
                                                    >
                                                        <EditOutlined fontSize="small" color="inherit"/>
                                                    </IconButton>
                                                </span>
                                                </Tooltip>
                                            </Box>
                                            <Box>
                                                <Tooltip title="Delete">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="inherit"
                                                        onClick={() => handleDeletePrompt(row?.id)}
                                                        aria-label="delete"
                                                    >
                                                        <DeleteOutlined fontSize="small" color="error"/>
                                                    </IconButton>
                                                </span>
                                                </Tooltip>
                                            </Box>
                                            {renewAccessToken(row)}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </IntegrationCard>
        </Box>
    );
}

function NoIntegrationsView({props}) {
    const addIntegration = () => props.history.push({pathname: '/settings/integrations/add', state: {background: props.location}});
    return <IntegrationEmptyCard title="Cost sync · Traffic sources" description="Sync advertising costs and manage campaigns from Skro." onAdd={addIntegration}
        features={['Automatically update traffic source costs.', 'Pause or activate campaigns, ad sets, and ads.',
            'Exclude zones, banners, and sites.', 'Adjust bids and daily budgets.']}/>;
}

const mapStateToProps = (state) => {
    return {
        alert: state.alert,
        integration: state.integration
    };
};

const mapDispatchToProps = {
    getTrafficSourceIntegrations: integrationActions.getTrafficSourceIntegrations,
    getAffiliateNetworkIntegrations: integrationActions.getAffiliateNetworkIntegrations,
    getRsocIntegrations: integrationActions.getRsocIntegrations,
    getServerIntegrations: integrationActions.getServerIntegrations,
    deleteIntegration: integrationActions.deleteIntegration,
    enableDataTransfer: integrationActions.enableDataTransfer,
    getRenewableFacebookLoginLink: integrationActions.getRenewableFacebookLoginLink,
    getRenewableTikTokLoginLink: integrationActions.getRenewableTikTokLoginLink,
    getRenewableGoogleLoginLink: integrationActions.getRenewableGoogleLoginLink,
    getRenewableMicrosoftLoginLink: integrationActions.getRenewableMicrosoftLoginLink,
    updateSelectedIntegration: integrationActions.updateSelectedIntegration,
    clear: alertActions.clear
};

export default connect(mapStateToProps, mapDispatchToProps)(Integrations);
